import React, { CSSProperties } from 'react'
import Checkbox from '@material-ui/core/Checkbox'

import moment from 'moment'
// @src imports
import {
  ContactType,
  Icon,
  RwdCell,
  RwdHeader,
  RwdRow,
  RwdTable,
} from 'src/chrome'
import { ContactFragment, OptionalYearDateInput } from 'src/graphql'

import styles from './contacts.module.scss'
import { Text } from '@sendoutcards/quantum-design-ui'

const selectAllStyles: CSSProperties = {
  textDecoration: 'underline',
  cursor: 'pointer',
}

const isSoon = (dateObj: OptionalYearDateInput) => {
  const a = moment([moment().year(), dateObj.month - 1, dateObj.day])
  const b = moment()
  const diff = a.diff(b, 'days')
  return diff < 21 && diff >= 0
}

interface AddressProps {
  contact: ContactFragment
}

const Address: React.FC<AddressProps> = props => {
  const { address1, address2, city, state, postalCode } = props.contact
  return (
    <div className={styles.Address}>
      <Text type="body">{`${address1}`}</Text>
      <Text type="body">{`${address2}`}</Text>
      <Text type="body">{`${city}, ${state} ${postalCode}`}</Text>
    </div>
  )
}

type HelperProps = {
  contact: ContactFragment
}

const Helper: React.FC<HelperProps> = props => {
  const { birthday, anniversary } = props.contact
  const isBirthdaySoon = !!birthday && isSoon(birthday)
  const isAnniversarySoon = !!anniversary && isSoon(anniversary)
  return (
    <>
      {(!birthday || !anniversary) &&
        false && ( // TODO: decide if we show contacts missing information
          <span style={{ margin: 5 }}>
            <Icon icon={'ALARM'} size={28} color={'#6f8394'} />
          </span>
        )}
      {isBirthdaySoon && (
        <span style={{ margin: 5 }}>
          <Icon
            icon={'CONFETTI'}
            size={28}
            color={'#6db0ff'}
            viewBox={'0 0 31 32'}
          />
        </span>
      )}
      {isAnniversarySoon && (
        <span style={{ margin: 5 }}>
          <Icon
            icon={'CHEERS'}
            size={28}
            color={'#F758A1'}
            viewBox={'0 0 33 32'}
          />
        </span>
      )}
    </>
  )
}

const formatDate = (
  dateObject: {
    day: number | null
    month: number | null
    year: number | null
  } | null,
) => {
  return dateObject && dateObject.year
    ? `${dateObject.month}/${dateObject.day}/${dateObject.year}`
    : dateObject
    ? `${dateObject.month}/${dateObject.day}`
    : ``
}

interface Props {
  isEveryContactSelected?: boolean
  contacts: ContactFragment[]
  goToContact: (id: string) => void
  handleSelectAllClick: () => void
  onSelect: (contact: ContactFragment) => void
  selectedContacts: Record<string, true>
}

const Contacts: React.FC<Props> = props => {
  const {
    isEveryContactSelected,
    contacts,
    goToContact,
    handleSelectAllClick,
    onSelect,
    selectedContacts,
  } = props

  return (
    <RwdTable>
      <RwdHeader>
        <th
          id={'select_all_btn'}
          style={selectAllStyles}
          onClick={() => handleSelectAllClick()}
        >
          <Text type="body" color="primaryBrand">
            {isEveryContactSelected ? 'Deselect All' : 'Select All'}
          </Text>
        </th>
        <th>
          <Text type="body" color="primaryBrand">
            Name
          </Text>
        </th>
        <th>
          <Text type="body" color="primaryBrand">
            Company Name
          </Text>
        </th>
        <th>
          <Text type="body" color="primaryBrand">
            Address
          </Text>
        </th>
        <th>
          <Text type="body" color="primaryBrand">
            Upcoming
          </Text>
        </th>
        <th>
          <Text type="body" color="primaryBrand">
            Birthday
          </Text>
        </th>
        <th>
          <Text type="body" color="primaryBrand">
            Anniversary
          </Text>
        </th>
      </RwdHeader>

      <tbody>
        {contacts.map((contact, i) => (
          <React.Fragment key={contact.id}>
            <RwdRow
              id={`contact_${i}`}
              style={
                contact.id in selectedContacts
                  ? { backgroundColor: '#e8e8e8' }
                  : {}
              }
            >
              <RwdCell mapToHeader="Select" className={styles.selectCell}>
                <div className={styles.contactActions}>
                  <Checkbox
                    id={`checkbox_${i}`}
                    name={contact.lastName}
                    checked={contact.id in selectedContacts}
                    onChange={() => onSelect(contact)}
                    className={styles.checkbox}
                  />
                </div>
              </RwdCell>
              <RwdCell mapToHeader="Name">
                <ContactType isPrimary={contact.isPrimary} />
                <div
                  style={{ cursor: 'pointer' }}
                  onClick={contact ? () => goToContact(contact.id) : () => {}}
                >
                  <Text type="body">
                    {`${contact.lastName}, ${contact.firstName}`}
                  </Text>
                </div>
              </RwdCell>
              <RwdCell mapToHeader="Company Name">
                <Text type="body">{contact.company}</Text>
              </RwdCell>
              <RwdCell mapToHeader="Address">
                <Address contact={contact} />
              </RwdCell>
              <RwdCell mapToHeader="Upcoming">
                <Helper contact={contact} />
              </RwdCell>
              <RwdCell mapToHeader="Birthday">
                <Text type="body">{formatDate(contact.birthday)}</Text>
              </RwdCell>
              <RwdCell mapToHeader="Anniversary">
                <Text type="body">{formatDate(contact.anniversary)}</Text>
              </RwdCell>
            </RwdRow>
            <tr className={styles.tableSpacer} />
          </React.Fragment>
        ))}
      </tbody>
    </RwdTable>
  )
}

export default Contacts
